import { useAppDispatch, useAppSelector } from "@redux_hooks/root_hook";
import { useCallback, useEffect } from "react";
import { Outlet, useNavigate } from "react-router";
import UIContainer from "./app/components/ui/UIContainer";
import useRouteInfo from "./app/hooks/useRouteInfo";
import { ROUTES } from "./app/routes";

import { useSetStaticAPIData } from "./app/hooks/api_hooks/process/useSetStaticAPIData";
import { useConfiguration } from "./app/hooks/useConfiguration";
import { useRBACFlagURL } from "./app/hooks/useRBACFlagURL";
import { useRouter } from "./app/hooks/useRouter";
import { useStringConstants } from "./app/hooks/useStringConstants";
import { setUIPage } from "./app_redux/reducers/slices/ui_page_slice";
import { setShowCommandPallete } from "./app_redux/reducers/slices/ui_states_slice";
import { logLogoutDiagnostic } from "@/api_framework/logoutDiagnostics";
import { BLUE7_BORIVALI_CLINIC_ID } from "@/constants";
import { useUpdateMirrorStateAPI } from "./app/hooks/api_hooks/Cart/useUpdateMirrorStateAPI";

function App() {
  // plugs checks whether the user has been authenticated or not if not then redirect to login page

  const { getRouteKey } = useRouter();
  const isValidLogin = useAppSelector((state) => state.auth.authorization.isValid) ?? false;
  const { loadView, userPermissions } = useRBACFlagURL(location.pathname);
  const { meta: UIMeta, routeState } = useRouteInfo(ROUTES.ROOT.id);
  const configuration = useConfiguration();
  const constants = useStringConstants();
  const dispatch = useAppDispatch();
  const setStaicAPIData = useSetStaticAPIData();
  const navigate = useNavigate();

  // Flattened list of every clinic the logged-in user has access to.
  const userClinics = useAppSelector(
    (state) => state.routes.auth.ORDER_MANAGEMENT.state.orderclinicList,
  );

  const { execute: updateMirrorState } = useUpdateMirrorStateAPI();

  // useCallback for useEffect dependencies
  const handleKeyPressed = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "k" && e.metaKey) {
        dispatch(setShowCommandPallete(true));
      }
      return;
    },
    [dispatch],
  );

  // Tracks keyboard events on the first mount
  useEffect(() => {
    let isMounted = false;
    if (!isMounted) {
      // Here is the mounting code for the whole application
      window.addEventListener("keydown", handleKeyPressed);
    }
    return () => {
      isMounted = true;
      window.removeEventListener("keydown", handleKeyPressed);
    };
  }, [handleKeyPressed]);

  // Load static API data that do not changes frequent here in Redux Store
  useEffect(() => {
    let isMounted = false;
    if (!isMounted) {
      /* This functions also triggers ApplicationBootLoader API 
      that checks application boot states
      */
      setStaicAPIData();
    }
    return () => {
      isMounted = true;
    };
  }, []);

  useEffect(() => {
    if (isValidLogin) {
      dispatch(
        setUIPage({
          ...UIMeta,
          title: routeState?.title,
          description: routeState?.description ?? "",
        }),
      );
    }
  }, [location]);

  useEffect(() => {
    if (userPermissions) {
      const cityId = localStorage.getItem(configuration.localStorage.CITY.key);
      const clinicId = localStorage.getItem(configuration.localStorage.CLINIC.key);
      if (loadView === "INVALID") {
        navigate(`${getRouteKey("INVALID_ACCESS", "url")}?redirect_uri=${location.href}`);
      }

      if (!cityId && !clinicId) {
        navigate(`${getRouteKey("MAPPED_CLINIC_AND_CITY", "url")}`);
      }
    }
  }, [userPermissions, loadView]);

  useEffect(() => {
    localStorage.setItem("sessionExpiry", null);
    localStorage.setItem("lastActivity", null);
  }, []);

  useEffect(() => {
    const INACTIVITY_LIMIT = 2 * 60 * 60 * 1000; // 2 hours in ms
    const LAST_ACTIVITY_KEY = "lastActivity";
    // Clinics where inactivity auto-logout is disabled
    const AUTO_LOGOUT_EXCLUDED_CLINICS = [
      BLUE7_BORIVALI_CLINIC_ID, // Blue7Vets, Borivali, Mumbai
    ];

    const checkExpiry = () => {
      const sessionExpiry = localStorage.getItem("sessionExpiry");
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);

      const now = Date.now();

      const doLogout = async () => {
        // If current tab is mirror-view, skip logout
        localStorage.setItem("VMS_LOGOUT_EVENT", Date.now().toString());
        await updateMirrorState({ screen: "idle", cart_id: null, status: "LOGGED_OUT" });

        // sessionStorage.clear();
        // localStorage.clear();
        // window.location.href = `/app/login`;
      };

      // 1. Session expiry check
      if (!sessionExpiry || now > Number(sessionExpiry)) {
        // doLogout();
        return;
      }

      // 2. Inactivity check
      if (lastActivity && now - Number(lastActivity) > INACTIVITY_LIMIT) {
        const currentClinicId = localStorage.getItem(configuration.localStorage.CLINIC.key);
        // Skip auto-logout only for excluded clinics AND when the user has access
        // to a single clinic (so multi-clinic users are unaffected).
        const hasSingleClinicAccess = userClinics?.length === 1;
        if (
          hasSingleClinicAccess &&
          currentClinicId &&
          AUTO_LOGOUT_EXCLUDED_CLINICS.includes(currentClinicId)
        ) {
          return;
        }
        // Diagnostic: confirm/deny that inactivity is what logs doctors out.
        logLogoutDiagnostic("INACTIVITY_TIMEOUT", {
          last_activity: lastActivity,
          inactive_ms: now - Number(lastActivity),
        });
        doLogout();
      }
    };

    const updateActivity = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    };

    // Track activity across tabs
    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("click", updateActivity);
    window.addEventListener("storage", checkExpiry); // cross-tab sync

    // Run immediately
    checkExpiry();
    updateActivity();

    // Check periodically (every minute)
    const interval = setInterval(checkExpiry, 60 * 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("click", updateActivity);
      window.removeEventListener("storage", checkExpiry);
    };
  }, [userClinics]);

  // Put this once in your app root (e.g., App.tsx or auth provider)
  useEffect(() => {
    const LOGOUT_KEY = "VMS_LOGOUT_EVENT";

    const doLocalLogout = async () => {
      await updateMirrorState({ screen: "idle", cart_id: null, status: "LOGGED_OUT" });
      localStorage.removeItem("VMS_LOGOUT_EVENT");

      if (window.location.href.includes("/mirror-view")) return;

      // Diagnostic: this is the point where the user is actually cleared out and
      // redirected to login. Logs the page it happened on (proves whether it was
      // mid-EMR). Correlate its timestamp with any AXIOS_REFRESH_FAILED event to
      // find the root trigger. Fired before clearing storage so user/clinic
      // context is still available.
      logLogoutDiagnostic("CROSS_TAB_FORCED_LOGOUT", {
        logout_event_value: localStorage.getItem(LOGOUT_KEY),
      });

      sessionStorage.clear();
      localStorage.clear();

      window.location.href = `/app/login`;
    };

    // --- Listen to broadcasted logout event
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOGOUT_KEY && e.newValue) {
        doLocalLogout();
      }
    };

    window.addEventListener("storage", handleStorage);

    // --- Backup: polling every 1s (to catch missed events)
    const interval = setInterval(() => {
      const pending = localStorage.getItem(LOGOUT_KEY);

      if (pending) {
        doLocalLogout();
      }
    }, 1000);

    // --- Cleanup
    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      {/* Keep the bootloader and breadcrumbs 'false' when in development */}

      {isValidLogin && (
        <UIContainer
          isValidLogin={isValidLogin}
          breadCrumbs={configuration.env.mode === constants.dev ? true : true}
          bootLoader={configuration.env.mode === constants.dev ? false : true}
        >
          <Outlet />
        </UIContainer>
      )}
      {!isValidLogin && <Outlet />}
    </>
  );
}

export default App;
